import React, { useState } from 'react';
import { DayOffer } from '../types';
import EnergyChart from './shared/EnergyChart';
import HybridBarChart from './shared/HybridBarChart';
import { Calendar } from 'lucide-react';

interface MultiDayOfferChartsProps {
  offers: DayOffer[];
  onConfigureChart?: () => void;
}

export const MultiDayOfferCharts: React.FC<MultiDayOfferChartsProps> = ({
  offers,
  onConfigureChart,
}) => {
  const [activeTab, setActiveTab] = useState<string>(offers[0]?.date || '');

  if (offers.length === 0) return null;

  // Sync activeTab if selected offers change
  const currentOffer = offers.find((o) => o.date === activeTab) || offers[0];

  // Single day hydro chart data (ΔΕΔΔΗΕ prediction removed)
  const singleDayAnoData = currentOffer.hourly.map((h) => ({
    id: h.hour,
    name: `Ώρα ${h.hour.toString().padStart(2, '0')}`,
    value1: h.mysAno,
    value2: 0,
  }));

  const singleDayKatoData = currentOffer.hourly.map((h) => ({
    id: h.hour,
    name: `Ώρα ${h.hour.toString().padStart(2, '0')}`,
    value1: h.mysKato,
    value2: 0,
  }));

  // Series definitions (No ΔΕΔΔΗΕ prediction)
  const anoSeries = [
    {
      key: 'value1',
      name: 'Προσφορά ΜΥΗΣ Προεσπέρας',
      color: '#3b82f6',
      gradient: 'linear-gradient(to right, #3b82f6, #1d4ed8)',
    },
  ];

  const katoSeries = [
    {
      key: 'value1',
      name: 'Προσφορά ΜΥΗΣ Κάτω Προεσπέρας',
      color: '#10b981',
      gradient: 'linear-gradient(to right, #10b981, #059669)',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Day Selector Pills Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-white p-3.5 shadow-sm">
        <div className="flex items-center gap-2 text-xs font-bold text-gray-700">
          <Calendar className="h-4 w-4 text-blue-600" />
          <span>Προβολή Γραφημάτων ανά Ημέρα:</span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {offers.map((o) => {
            const isActive = currentOffer.date === o.date;
            return (
              <button
                key={o.date}
                onClick={() => setActiveTab(o.date)}
                className={`rounded-xl px-4 py-1.5 text-xs font-bold transition-all duration-200 ${
                  isActive
                    ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
                }`}
              >
                {o.dayLabel}
              </button>
            );
          })}
        </div>
      </div>

      {/* Grid Layout for Charts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Hydro Proesperas Chart */}
        <div>
          <EnergyChart
            data={singleDayAnoData}
            title="Δήλωση Υδροηλεκτρικής ΜΥΗΣ Προεσπέρας"
            subtitle={
              currentOffer.mode === 'summer_mode'
                ? `Ξηρή Περίοδος – Εκτός λειτουργίας (${currentOffer.dayLabel})`
                : `Υγρή Περίοδος – Προσφορά ΜΥΗΣ (${currentOffer.dayLabel})`
            }
            series={anoSeries}
            height={260}
            onConfigure={onConfigureChart}
          />
        </div>

        {/* Hydro Lower Proesperas Chart */}
        <div>
          <EnergyChart
            data={singleDayKatoData}
            title="Δήλωση Υδροηλεκτρικής ΜΥΗΣ Κάτω Προεσπέρας"
            subtitle={
              currentOffer.mode === 'summer_mode'
                ? `Ξηρή Περίοδος – Εκτός λειτουργίας (${currentOffer.dayLabel})`
                : `Υγρή Περίοδος – Προσφορά ΜΥΗΣ (${currentOffer.dayLabel})`
            }
            series={katoSeries}
            height={260}
            onConfigure={onConfigureChart}
          />
        </div>

        {/* Hybrid 1st & 2nd 12h Bar Chart (Full width span, 2 bars in MWh) */}
        <div className="lg:col-span-2">
          <HybridBarChart
            first12hMwh={currentOffer.hybrid1st12h}
            second12hMwh={currentOffer.hybrid2nd12h}
            dayLabel={currentOffer.dayLabel}
            height={260}
            onConfigure={onConfigureChart}
          />
        </div>
      </div>
    </div>
  );
};

export default MultiDayOfferCharts;

